import { useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import MessageInput from "@/components/chat/MessageInput";
import MessageList from "@/components/chat/MessageList";
import { PageHeader, PageLayout } from "@/components/layout";
import { AppCard } from "@/components/ui";
import { useInvestorChat } from "@/hooks/useInvestorChat";
import { formatUsageCost, formatUsageTokens } from "@/utils/aiUsage";

const ThinkingIndicator = () => (
  <div className="flex items-center gap-2 text-sm text-muted-foreground">
    <span>Thinking</span>
    <span className="flex gap-2">
      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" />
      <span
        className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground"
        style={{ animationDelay: "150ms" }}
      />
      <span
        className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground"
        style={{ animationDelay: "300ms" }}
      />
    </span>
  </div>
);

function useInvestorChatSearch() {
  const routerSearch = useSearch({
    from: "/app/companies/$company_id/sections/$section_id/investor-chat/$thread_id",
  });

  return useMemo(() => {
    return {
      founder_company_id: typeof routerSearch?.founder_company_id === "string" ? routerSearch.founder_company_id : "",
      founder_name: typeof routerSearch?.founder_name === "string" ? routerSearch.founder_name : "Deal",
      initial_prompt:
        typeof routerSearch?.initial_prompt === "string"
          ? routerSearch.initial_prompt
          : routerSearch?.initial_prompt === null
            ? null
            : undefined,
    };
  }, [routerSearch]);
}

/**
 * Investor chat page - full-page chat for deal pipeline questions.
 * Uses the investor chat WebSocket API (deal_pipeline section).
 */
export default function InvestorChatPage() {
  const params = useParams({ strict: false });
  const search = useInvestorChatSearch();

  const companyId = params.company_id;
  const threadId = params.thread_id;
  const founderCompanyId = search.founder_company_id;
  const founderName = search.founder_name;
  const initialPrompt = search.initial_prompt;

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { messages, status, error, isThinking, latestUsage, sendMessage } = useInvestorChat({
    investorCompanyId: companyId ?? null,
    founderCompanyId,
    threadId: threadId ?? null,
    enabled: !!companyId && !!founderCompanyId && !!threadId,
    initialPrompt,
  });

  useEffect(() => {
    if (messages.length > 0 || isThinking) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 50);
      });
    }
  }, [messages, isThinking]);

  const handleSend = (content: string) => {
    sendMessage(content);
  };

  return (
    <PageLayout>
      <PageHeader
        title={`Deal Pipeline: ${founderName}`}
        subtitle="Investor Chat"
      />
      <AppCard
        variant="flat"
        padding="none"
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <div className="border-b border-border px-4 py-4">
          <p className="text-xs text-muted-foreground">
            {status === "connecting" && "Connecting…"}
            {status === "connected" && "Connected"}
            {status === "idle" && ""}
            {status === "error" && "Disconnected"}
          </p>
          {latestUsage && (
            <p className="text-xs text-muted-foreground">
              AI usage: {formatUsageCost(latestUsage.totalCost)} | {formatUsageTokens(latestUsage.totalTokens)} tokens
            </p>
          )}
          {import.meta.env.DEV && (
            <p className="text-xs text-muted-foreground/70">
              Debug: {messages.length} messages | thinking: {String(isThinking)} | company: {founderCompanyId || "none"}
            </p>
          )}
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        </div>
        <div className="flex-1 overflow-y-auto">
          {messages.length === 0 && !isThinking && (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <p>No messages yet. Start the conversation!</p>
            </div>
          )}
          <MessageList
            messages={messages}
            variant="split"
          />
          {isThinking && (
            <div className="px-4 py-4">
              <ThinkingIndicator />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <MessageInput
          onSend={handleSend}
          disabled={status === "connecting"}
          placeholder={`Ask about ${founderName}...`}
        />
      </AppCard>
    </PageLayout>
  );
}
